import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';
export type NotificationMode = 'toast' | 'alert' | 'confirm';

export interface NotificationItem {
  id: number;
  mode: NotificationMode;
  type: NotificationType;
  title: string;
  message: string;
  confetti: boolean;
  autoClose: boolean;
  autoCloseMs: number;
  confirmText?: string;
  denyText?: string;
  /** Si viene true, el modal de confirmación pide un texto obligatorio (ver confirmWithInput). */
  requireInput?: boolean;
  inputPlaceholder?: string;
  /** Enlazado con [(ngModel)] en el textarea del modal — mutado directamente por la plantilla. */
  inputValue?: string;
  resolve?: (confirmed: boolean) => void;
}

export interface ConfirmOptions {
  title: string;
  message?: string;
  type?: NotificationType;
  confirmText?: string;
  denyText?: string;
}

export interface ConfirmWithInputOptions extends ConfirmOptions {
  inputPlaceholder: string;
}

let nextId = 0;

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  private notificationsSubject = new BehaviorSubject<NotificationItem[]>([]);
  notifications$: Observable<NotificationItem[]> = this.notificationsSubject.asObservable();

  /**
   * Muestra un toast temporal (top-right, auto-dismiss).
   */
  toast(message: string, type: NotificationType = 'success', durationMs: number = 3000): void {
    this.push({
      id: ++nextId,
      mode: 'toast',
      type,
      title: message,
      message: '',
      confetti: false,
      autoClose: true,
      autoCloseMs: durationMs
    });
  }

  /**
   * Muestra una alerta modal centrada (requiere dismiss manual).
   */
  alert(title: string, message: string, type: NotificationType = 'error'): void {
    this.push({
      id: ++nextId,
      mode: 'alert',
      type,
      title,
      message,
      confetti: false,
      autoClose: false,
      autoCloseMs: 0
    });
  }

  /**
   * Muestra un diálogo de confirmación y retorna una Promise<boolean>.
   */
  confirm(options: ConfirmOptions): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      this.push({
        id: ++nextId,
        mode: 'confirm',
        type: options.type || 'warning',
        title: options.title,
        message: options.message || '',
        confetti: false,
        autoClose: false,
        autoCloseMs: 0,
        confirmText: options.confirmText || 'Confirmar',
        denyText: options.denyText || 'Cancelar',
        resolve
      });
    });
  }

  /**
   * Igual que confirm(), pero exige un texto (p.ej. una justificación) antes de poder confirmar.
   * Resuelve con el texto (recortado) si se confirma, o null si se cancela.
   */
  confirmWithInput(options: ConfirmWithInputOptions): Promise<string | null> {
    return new Promise<string | null>(resolve => {
      const item: NotificationItem = {
        id: ++nextId,
        mode: 'confirm',
        type: options.type || 'warning',
        title: options.title,
        message: options.message || '',
        confetti: false,
        autoClose: false,
        autoCloseMs: 0,
        confirmText: options.confirmText || 'Confirmar',
        denyText: options.denyText || 'Cancelar',
        requireInput: true,
        inputPlaceholder: options.inputPlaceholder,
        inputValue: '',
        resolve: confirmed => resolve(confirmed ? (item.inputValue || '').trim() : null)
      };
      this.push(item);
    });
  }

  /**
   * Muestra éxito con animación Lottie + confetti opcional.
   */
  celebrateSuccess(message: string, confetti: boolean = false, durationMs: number = 2000): void {
    this.push({
      id: ++nextId,
      mode: 'toast',
      type: 'success',
      title: message,
      message: '',
      confetti,
      autoClose: true,
      autoCloseMs: durationMs
    });
  }

  /**
   * Remueve una notificación del stack por ID.
   */
  dismiss(id: number): void {
    const current = this.notificationsSubject.getValue();
    this.notificationsSubject.next(current.filter(n => n.id !== id));
  }

  private push(item: NotificationItem): void {
    const current = this.notificationsSubject.getValue();
    this.notificationsSubject.next([...current, item]);
  }
}
